import { Product } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { ProductQuantityType } from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import { calculateAppliedAcres } from '../components/FillProductDetailsDrawer/helpers/calculateAcreage';
import { calculateProductByQuantityMode } from './calculateProduct';

/**
 * Recalculates product values when properties (total applied acreage, application method) change
 * Maintains coverage percentage and recalculates applied acres and total product
 *
 * @param products - Array of products to recalculate
 * @param totalAppliedAcreage - New total applied acreage from the application
 * @param options - Optional parameters including tankCount and applicationMethod
 * @returns Updated array of products with recalculated values
 */
// eslint-disable-next-line import/prefer-default-export
export const recalculateProductsOnPropertiesChange = (
  products: Product[],
  totalAppliedAcreage: number,
  {
    tankCount,
    applicationMethod
  }: { tankCount?: number; applicationMethod?: string } = {},
  convertFn?: Function
): Product[] => {
  if (!totalAppliedAcreage || !products || products.length === 0) {
    return products;
  }

  return products.map(product => {
    if (product.isCarrier) return product;

    // Only recompute appliedAreaValue when totalAppliedAcreage has actually
    // changed since this product was last calculated. The product stores its
    // own totalAppliedAcreage (set further down), so we can detect a real
    // change vs a no-op call (e.g. tank info toggle, which fires the
    // ProductsStep useEffect without actually changing acres). On a no-op
    // we preserve the user's exact saved appliedAreaValue, avoiding the
    // floating-point drift that would otherwise creep in via
    // (totalAppliedAcreage × coveragePercent / 100) where coveragePercent
    // may itself carry sub-decimal drift from a prior division.
    const totalChanged =
      (product.totalAppliedAcreage ?? 0) !== totalAppliedAcreage;
    const newAppliedAcres = totalChanged
      ? calculateAppliedAcres(
          product.coveragePercent || 100,
          totalAppliedAcreage || 0
        )
      : product.appliedAreaValue ?? 0;

    const calculatedValues = calculateProductByQuantityMode(
      (product.quantityMode ||
        'ratePerAcre') as keyof typeof ProductQuantityType,
      newAppliedAcres,
      product,
      { tankInformation: { tankCount } },
      convertFn
    );

    return {
      ...product,
      appliedAreaValue: newAppliedAcres,
      totalAppliedAcreage,
      // Update applicationMethod from application level if provided
      ...(applicationMethod !== undefined && { applicationMethod }),
      ...(calculatedValues
        ? {
            totalProduct: calculatedValues.totalProductValue,
            ratePerAcre: calculatedValues.ratePerAreaValue,
            ratePerTank: calculatedValues.ratePerTankValue,
            totalCost: calculatedValues.totalCostAtTimeOfCreation || 0
          }
        : {})
    };
  });
};
